import { Link } from "react-router-dom";
import LegalDocumentPage from "@/components/legal/LegalDocumentPage";
import {
  RADAR_LEGAL_ENTITY,
  RADAR_JURISDICTION,
  ROMANIA_DPA_FULL_NAME,
  ROMANIA_DPA_SHORT,
  ROMANIA_DPA_WEBSITE,
  ROMANIA_GDPR_IMPLEMENTING_LAW,
} from "@/lib/legalEntity";

export default function GdprPage() {
  return (
    <LegalDocumentPage
      title="GDPR & EU data rights"
      description="GDPR rights and Romanian data protection (ANSPDCP, Law 190/2018) for Radar users in the EU, UK, and EEA."
      canonical="/gdpr"
    >
      <p>
        This page supplements our{" "}
        <Link to="/privacy" className="text-primary hover:underline">
          Privacy Policy
        </Link>{" "}
        for users in the European Economic Area (EEA), Switzerland, and the United Kingdom (UK), where the General Data
        Protection Regulation (GDPR) or equivalent UK data protection law applies. {RADAR_LEGAL_ENTITY} is established in{" "}
        {RADAR_JURISDICTION}; Romanian users are also covered by {ROMANIA_GDPR_IMPLEMENTING_LAW}.
      </p>

      <h2>Data controller</h2>
      <p>
        The data controller responsible for personal data processed in connection with Radar is {RADAR_LEGAL_ENTITY}
        (trading as Aquivo Labs), a company incorporated in {RADAR_JURISDICTION} that owns and operates Radar. Our
        processing is subject to the GDPR and, where applicable, {ROMANIA_GDPR_IMPLEMENTING_LAW}. For requests about your
        personal data, use the support channel listed on the Radar listing in the Shopify App Store and reference “GDPR”
        or “data protection.”
      </p>

      <h2>Romania: supervisory authority and national law</h2>
      <p>
        If you are in {RADAR_JURISDICTION}, the lead supervisory authority for our processing as a controller
        established in Romania is typically the {ROMANIA_DPA_FULL_NAME} ({ROMANIA_DPA_SHORT}). You may lodge a complaint
        with {ROMANIA_DPA_SHORT}:{" "}
        <a href={ROMANIA_DPA_WEBSITE} className="text-primary hover:underline" target="_blank" rel="noopener noreferrer">
          {ROMANIA_DPA_WEBSITE.replace(/^https:\/\//, "")}
        </a>
        . National rules in {ROMANIA_GDPR_IMPLEMENTING_LAW} complement the GDPR (for example, provisions on processing
        and cooperation with the authority).
      </p>

      <h2>Categories of data</h2>
      <p>
        We process the categories of personal data summarized in the Privacy Policy, including account and
        authentication data, shop configuration, usage and technical data, and communications. We do not use automated
        decision-making that produces legal or similarly significant effects solely by automated means without human
        involvement, unless we separately inform you and provide required safeguards.
      </p>

      <h2>Purposes and legal bases</h2>
      <p>We process personal data for purposes including:</p>
      <ul>
        <li>
          <strong>Providing the Service</strong> — legal basis: performance of a contract with you (Art. 6(1)(b) GDPR).
        </li>
        <li>
          <strong>Security, abuse prevention, and service improvement</strong> — legal basis: legitimate interests
          (Art. 6(1)(f) GDPR), balanced against your rights.
        </li>
        <li>
          <strong>Marketing communications</strong> — where required, consent (Art. 6(1)(a) GDPR) or soft opt-in where
          permitted by law.
        </li>
        <li>
          <strong>Legal compliance</strong> — legal basis: legal obligation (Art. 6(1)(c) GDPR) or legitimate interests
          where applicable.
        </li>
      </ul>

      <h2>Recipients and transfers</h2>
      <p>
        We share personal data with subprocessors (such as hosting and email providers) under data processing
        agreements. Some subprocessors may be located outside the EEA/UK. Where we transfer personal data to countries
        not subject to an adequacy decision, we rely on appropriate safeguards, including Standard Contractual Clauses
        approved by the European Commission and, for the UK, the UK Addendum or UK International Data Transfer Agreement
        as applicable.
      </p>

      <h2>Retention</h2>
      <p>
        We retain personal data only as long as necessary for the purposes described in the Privacy Policy, unless a
        longer period is required by law. Criteria include the nature of the data, ongoing business need, and legal
        obligations.
      </p>

      <h2>Your rights</h2>
      <p>Under GDPR and UK law, you may have the right to:</p>
      <ul>
        <li>
          <strong>Access</strong> — obtain confirmation of processing and a copy of your personal data (Art. 15).
        </li>
        <li>
          <strong>Rectification</strong> — correct inaccurate data (Art. 16).
        </li>
        <li>
          <strong>Erasure</strong> — request deletion in certain circumstances (Art. 17).
        </li>
        <li>
          <strong>Restriction</strong> — limit processing in certain circumstances (Art. 18).
        </li>
        <li>
          <strong>Data portability</strong> — receive data you provided in a structured, machine-readable format where
          technically feasible (Art. 20).
        </li>
        <li>
          <strong>Object</strong> — object to processing based on legitimate interests or for direct marketing (Art. 21).
        </li>
        <li>
          <strong>Withdraw consent</strong> — where processing is based on consent, without affecting prior lawful
          processing (Art. 7(3)).
        </li>
        <li>
          <strong>Lodge a complaint</strong> — with a supervisory authority (Art. 77), in particular in the country
          where you live, work, or where an alleged infringement occurred. If you are in Romania, you may contact{" "}
          {ROMANIA_DPA_SHORT}. If you are in another EEA country, you may contact your local supervisory authority.
        </li>
      </ul>
      <p>
        To exercise these rights, contact us through the support channel on the Radar Shopify App Store listing. We may
        need to verify your identity before responding. You will not usually have to pay a fee; we will respond within
        one month unless the request is complex, in which case we may extend as permitted by law.
      </p>

      <h2>Data Protection Officer</h2>
      <p>
        Under the GDPR and {ROMANIA_GDPR_IMPLEMENTING_LAW}, we must appoint a Data Protection Officer only where required
        (for example, certain large-scale or sensitive processing). If we designate a DPO or a representative in another
        EU country for Radar, we will publish updated contact details on this page or in the Privacy Policy.
      </p>

      <h2>UK GDPR</h2>
      <p>
        After Brexit, the UK GDPR and Data Protection Act 2018 apply to UK residents. The rights and protections
        described here apply accordingly, and references to GDPR include the UK GDPR where relevant.
      </p>
    </LegalDocumentPage>
  );
}
